import React, { Component, PropTypes } from 'react';
import moment from 'moment';
import idx from 'idx';
import partial from 'lodash/partial';
import without from 'lodash/without';
import omit from 'lodash/omit';
import {
  TYPE_CHECKBOXES, TYPE_SELECT, TYPE_RANGE, TYPE_RANGE_SELECT, TYPE_STRING
} from 'common/filter/constants';
import { getQueryFromValue } from 'common/filter/utils';
import { getQueryFromValue as getPagerQueryFromValue } from 'common/pager/utils';

/**
 * Applied filter tags.
 * Filter values are always read/updated from/to the URL query parameters.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class Applied extends Component {
  constructor(props) {
    super(props);

    this.updateQuery = this.updateQuery.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.removeAll = this.removeAll.bind(this);
  }

  removeAll() {
    const { router, queryKeys, resourceName } = this.props;
    const currentLocation = router.getCurrentLocation();

    this.updateQuery(currentLocation, omit(currentLocation.query, queryKeys));
  }

  handleChange(value, field, type) {
    const { router, resourceName } = this.props;
    const currentLocation = router.getCurrentLocation();
    const filterQuery = getQueryFromValue(resourceName, field, type, value);
    const updatedQuery = {
      ...currentLocation.query,
      ...filterQuery
    };

    this.updateQuery(currentLocation, updatedQuery);
  }

  updateQuery(currentLocation, updatedQuery) {
    const { resourceName, router } = this.props;
    /* clear page when updating filters */
    const pagerQuery = getPagerQueryFromValue(resourceName, '');
    router.push({
      ...currentLocation,
      query: {
        ...updatedQuery,
        ...pagerQuery
      }
    });
  }

  getFieldLabel(field) {
    return idx(this.props.fieldLabels, _ => _[field]) || field;
  }

  getCheckboxesTags({ field, type, value }) {
    const options = idx(this.props.options, _ => _[field]) || [];
    const fieldLabel = this.getFieldLabel(field);

    return value.reduce((tags, fieldValue) => {
      const option = options.find(opt => opt.field === fieldValue);
      const label = option? option.label : fieldValue;

      return tags.concat({
        content: `${fieldLabel}=${label}`,
        onRemove: partial(this.handleChange, without(value, fieldValue), field, type)
      })
    }, []);
  }

  getSelectTags({ field, type, value }) {
    const options = idx(this.props.options, _ => _[field]) || [];
    const option = options.find(opt => opt.value == value);
    const label = option? option.label : value;
    const fieldLabel = this.getFieldLabel(field);

    return [{
      content: `${fieldLabel}=${label}`,
      onRemove: partial(this.handleChange, undefined, field, type)
    }];
  }

  getRangeTags({ field, type, value }) {
    return Object.keys(value).reduce((tags, key) => {
      const fieldLabel = this.getFieldLabel(field);
      const separator = key === 'start'? '>' : '<';
      const isDate = moment(value[key], moment.ISO_8601, true).isValid();
      const fieldValue = isDate? moment(value[key]).format("MM/DD/YYYY") : value[key];

      if (!fieldValue) {
        return tags;
      }

      return tags.concat({
        content: `${fieldLabel}${separator}${fieldValue}`,
        onRemove: partial(this.handleChange, omit(value, key), field, type)
      })
    }, []);
  }

  getStringTags({ field, type, value }) {
    const fieldLabel = this.getFieldLabel(field);

    return [{
      content: `${fieldLabel}=${value}`,
      onRemove: partial(this.handleChange, undefined, field, type)
    }];
  }

  getTags() {
    return this.props.filters.reduce((allTags, filter) => {
      let tags = [];

      if (filter.type === TYPE_CHECKBOXES) {
        tags = this.getCheckboxesTags(filter);
      } else if ([TYPE_SELECT, TYPE_RANGE_SELECT].includes(filter.type)) {
        tags = this.getSelectTags(filter);
      } else if (filter.type === TYPE_RANGE) {
        tags = this.getRangeTags(filter);
      } else if (filter.type === TYPE_STRING) {
        tags = this.getStringTags(filter);
      }

      return allTags.concat(tags);
    }, []);
  }

  render() {
    const tags = this.getTags();

    return (
      <div className="applied-filters">
        { tags.map((tag, i) => (
            <div className="tag tag-sm tag-highlight" key={i} onClick={tag.onRemove} >
              { tag.content } <i className="fa fa-close" />
            </div>
        )) }

        { tags.length > 0 &&
        <div className="tag tag-sm tag-highlight warning" onClick={this.removeAll}>
          Clear All
        </div> }
      </div>
    );
  }
};

Applied.propTypes = {
  router: PropTypes.object.isRequired,
  resourceName: PropTypes.string.isRequired,
  filters: PropTypes.array.isRequired,
  queryKeys: PropTypes.array.isRequired,
  /* used only for display purposes, i.e. if (for fields that accept options) we want to show
     the label instead of the field value. E.g. `category=Logo Design` instead of `category=5`. */
  options: PropTypes.object,
  /* used only for display purposes, i.e. if we want to show a different label for a given field.
     E.g. `Total Award=500` instead of `total_awards_and_tips=500`. */
  fieldLabels: PropTypes.object,
};



// WEBPACK FOOTER //
// ./common/filter/components/applied.jsx